import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as THREE from 'three';

type ColorBendsProps = {
  className?: string;
  style?: React.CSSProperties;
  rotation?: number;
  speed?: number;
  colors?: string[];
  transparent?: boolean;
  autoRotate?: number;
  scale?: number;
  frequency?: number;
  warpStrength?: number;
  noise?: number;
  centerRadius?: number;
  centerFalloff?: number;
  ascii?: boolean;
  asciiCellSize?: number;
  debug?: boolean;
};

const MAX_COLORS = 8 as const;

const ASCII_CHARS = ' .+#';

const frag = `
#define MAX_COLORS ${MAX_COLORS}
uniform vec2 uCanvas;
uniform float uTime;
uniform float uSpeed;
uniform vec2 uRot;
uniform int uColorCount;
uniform vec3 uColors[MAX_COLORS];
uniform int uTransparent;
uniform float uScale;
uniform float uFrequency;
uniform float uWarpStrength;
uniform float uNoise;
uniform float uCenterRadius;
uniform float uCenterFalloff;
varying vec2 vUv;

// SDF for rounded rectangle (the hero content area)
float sdRoundedBox(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

void main() {
  float t = uTime * uSpeed;
  vec2 p = vUv * 2.0 - 1.0;

  float aspect = uCanvas.x / uCanvas.y;
  vec2 centerP = vec2(p.x * aspect, p.y);

  vec2 boxSize = vec2(uCenterRadius * aspect, uCenterRadius * 0.6);
  float d = sdRoundedBox(centerP, boxSize, 0.1);

  float repulse = 1.0 - smoothstep(-uCenterFalloff, uCenterFalloff * 0.5, d);
  vec2 dir = length(centerP) > 0.001 ? normalize(centerP) : vec2(0.0, 1.0);
  p += dir * repulse * uCenterFalloff * 1.5;

  vec2 rp = vec2(p.x * uRot.x - p.y * uRot.y, p.x * uRot.y + p.y * uRot.x);
  vec2 q = vec2(rp.x * aspect, rp.y);
  q /= max(uScale, 0.0001);
  q /= 0.5 + 0.2 * dot(q, q);
  q += 0.2 * cos(t) - 7.56;

  vec3 col = vec3(0.0);
  float a = 1.0;

  if (uColorCount > 0) {
    vec2 s = q;
    vec3 sumCol = vec3(0.0);
    float cover = 0.0;
    for (int i = 0; i < MAX_COLORS; ++i) {
      if (i >= uColorCount) break;
      s -= 0.01;
      vec2 r = sin(1.5 * (s.yx * uFrequency) + 2.0 * cos(s * uFrequency));
      float m0 = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(i)) / 4.0);
      float kBelow = clamp(uWarpStrength, 0.0, 1.0);
      float kMix = pow(kBelow, 0.3); // strong response across 0..1
      float gain = 1.0 + max(uWarpStrength - 1.0, 0.0); // allow >1 to amplify displacement
      vec2 disp = (r - s) * kBelow;
      vec2 warped = s + disp * gain;
      float m1 = length(warped + sin(5.0 * warped.y * uFrequency - 3.0 * t + float(i)) / 4.0);
      float m = mix(m0, m1, kMix);
      float w = 1.0 - exp(-6.0 / exp(6.0 * m));
      sumCol += uColors[i] * w;
      cover = max(cover, w);
    }
    col = clamp(sumCol, 0.0, 1.0);
    a = uTransparent > 0 ? cover : 1.0;
  } else {
    vec2 s = q;
    for (int k = 0; k < 3; ++k) {
      s -= 0.01;
      vec2 r = sin(1.5 * (s.yx * uFrequency) + 2.0 * cos(s * uFrequency));
      float m0 = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(k)) / 4.0);
      float kBelow = clamp(uWarpStrength, 0.0, 1.0);
      float kMix = pow(kBelow, 0.3);
      float gain = 1.0 + max(uWarpStrength - 1.0, 0.0);
      vec2 disp = (r - s) * kBelow;
      vec2 warped = s + disp * gain;
      float m1 = length(warped + sin(5.0 * warped.y * uFrequency - 3.0 * t + float(k)) / 4.0);
      float m = mix(m0, m1, kMix);
      col[k] = 1.0 - exp(-6.0 / exp(6.0 * m));
    }
    a = uTransparent > 0 ? max(max(col.r, col.g), col.b) : 1.0;
  }

  if (uNoise > 0.0001) {
    float n = fract(sin(dot(gl_FragCoord.xy + vec2(uTime), vec2(12.9898, 78.233))) * 43758.5453123);
    col += (n - 0.5) * uNoise;
    col = clamp(col, 0.0, 1.0);
  }

  if (uCenterRadius > 0.0) {
    vec2 origP = vUv * 2.0 - 1.0;
    vec2 origCenterP = vec2(origP.x * (uCanvas.x / uCanvas.y), origP.y);
    vec2 maskBoxSize = vec2(uCenterRadius * (uCanvas.x / uCanvas.y), uCenterRadius * 0.6);
    float maskD = sdRoundedBox(origCenterP, maskBoxSize, 0.15);
    float centerAlpha = smoothstep(-0.02, uCenterFalloff, maskD);
    a *= centerAlpha;
  }

  vec3 rgb = (uTransparent > 0) ? col * a : col;
  gl_FragColor = vec4(rgb, a);
}
`;

const asciiFrag = `
uniform sampler2D uScene;
uniform sampler2D uFontAtlas;
uniform vec2 uCanvas;
uniform vec2 uCellSize;
uniform float uCharCount;
uniform int uTransparent;
varying vec2 vUv;

void main() {
  vec2 cellCount = floor(uCanvas / uCellSize);
  vec2 cellIndex = floor(vUv * cellCount);
  vec2 cellUv = fract(vUv * cellCount);

  vec2 sampleUv = (cellIndex + 0.5) / cellCount;
  vec2 texelSize = 1.0 / cellCount;

  vec4 sceneColor = vec4(0.0);
  float totalWeight = 0.0;
  for (float dy = -1.0; dy <= 1.0; dy += 1.0) {
    for (float dx = -1.0; dx <= 1.0; dx += 1.0) {
      vec2 offset = vec2(dx, dy) * texelSize * 0.3;
      float weight = 1.0 - length(vec2(dx, dy)) * 0.2;
      sceneColor += texture2D(uScene, sampleUv + offset) * weight;
      totalWeight += weight;
    }
  }
  sceneColor /= totalWeight;

  float brightness = dot(sceneColor.rgb, vec3(0.299, 0.587, 0.114));

  brightness = pow(brightness, 0.7);

  float charIndex;
  if (brightness < 0.15) {
    charIndex = 0.0;
  } else if (brightness < 0.35) {
    charIndex = 1.0;
  } else if (brightness < 0.6) {
    charIndex = 2.0;
  } else {
    charIndex = 3.0;
  }

  float charWidth = 1.0 / uCharCount;
  vec2 atlasUv = vec2(
    (charIndex + cellUv.x) * charWidth,
    cellUv.y
  );

  vec4 charColor = texture2D(uFontAtlas, atlasUv);

  // Output: character alpha * scene color for tinted ASCII
  float alpha = charColor.a * sceneColor.a;
  vec3 finalColor = sceneColor.rgb;

  if (uTransparent > 0) {
    gl_FragColor = vec4(finalColor * alpha, alpha);
  } else {
    gl_FragColor = vec4(finalColor * charColor.a, 1.0);
  }
}
`;

const vert = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

// Generate font atlas texture with ASCII characters
function createFontAtlas(chars: string, cellWidth: number, cellHeight: number): THREE.Texture {
  const canvas = document.createElement('canvas');
  const charCount = chars.length;
  canvas.width = cellWidth * charCount;
  canvas.height = cellHeight;

  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Use monospace font
  const fontSize = Math.floor(cellHeight * 0.9);
  ctx.font = `${fontSize}px "SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'white';

  for (let i = 0; i < charCount; i++) {
    const x = i * cellWidth + cellWidth / 2;
    const y = cellHeight / 2;
    ctx.fillText(chars[i], x, y);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;

  return texture;
}

export default function ColorBends({
  className,
  style,
  rotation: rotationProp = 0,
  speed: speedProp = 0.2,
  colors: colorsProp = ['#ff5c7a', '#8a5cff', '#00ffd1'],
  transparent: transparentProp = true,
  autoRotate: autoRotateProp = 0,
  scale: scaleProp = 1,
  frequency: frequencyProp = 1,
  warpStrength: warpStrengthProp = 1,
  noise: noiseProp = 0.1,
  centerRadius: centerRadiusProp = 0,
  centerFalloff: centerFalloffProp = 0.3,
  ascii: asciiProp = false,
  asciiCellSize: asciiCellSizeProp = 12,
  debug = false,
}: ColorBendsProps) {
  // Debug state - uses props as initial values
  const [dSpeed, setDSpeed] = useState(speedProp);
  const [dScale, setDScale] = useState(scaleProp);
  const [dFrequency, setDFrequency] = useState(frequencyProp);
  const [dWarpStrength, setDWarpStrength] = useState(warpStrengthProp);
  const [dNoise, setDNoise] = useState(noiseProp);
  const [dCenterRadius, setDCenterRadius] = useState(centerRadiusProp);
  const [dCenterFalloff, setDCenterFalloff] = useState(centerFalloffProp);
  const [dAscii, setDAscii] = useState(asciiProp);
  const [dAsciiCellSize, setDAsciiCellSize] = useState(asciiCellSizeProp);

  // Use debug values if debug mode, otherwise use props
  const rotation = rotationProp;
  const speed = debug ? dSpeed : speedProp;
  const colors = colorsProp;
  const transparent = transparentProp;
  const autoRotate = autoRotateProp;
  const scale = debug ? dScale : scaleProp;
  const frequency = debug ? dFrequency : frequencyProp;
  const warpStrength = debug ? dWarpStrength : warpStrengthProp;
  const noise = debug ? dNoise : noiseProp;
  const centerRadius = debug ? dCenterRadius : centerRadiusProp;
  const centerFalloff = debug ? dCenterFalloff : centerFalloffProp;
  const ascii = debug ? dAscii : asciiProp;
  const asciiCellSize = debug ? dAsciiCellSize : asciiCellSizeProp;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const rafRef = useRef<number | null>(null);
  const materialRef = useRef<THREE.ShaderMaterial | null>(null);
  const asciiMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
  const renderTargetRef = useRef<THREE.WebGLRenderTarget | null>(null);
  const rotationRef = useRef<number>(rotation);
  const autoRotateRef = useRef<number>(autoRotate);
  const fontAtlasRef = useRef<THREE.Texture | null>(null);

  useEffect(() => {
    const container = containerRef.current!;
    const scene = new THREE.Scene();
    const asciiScene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const geometry = new THREE.PlaneGeometry(2, 2);
    const uColorsArray = Array.from({ length: MAX_COLORS }, () => new THREE.Vector3(0, 0, 0));
    const material = new THREE.ShaderMaterial({
      vertexShader: vert,
      fragmentShader: frag,
      uniforms: {
        uCanvas: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0 },
        uSpeed: { value: speed },
        uRot: { value: new THREE.Vector2(1, 0) },
        uColorCount: { value: 0 },
        uColors: { value: uColorsArray },
        uTransparent: { value: transparent ? 1 : 0 },
        uScale: { value: scale },
        uFrequency: { value: frequency },
        uWarpStrength: { value: warpStrength },
        uNoise: { value: noise },
        uCenterRadius: { value: centerRadius },
        uCenterFalloff: { value: centerFalloff },
      },
      premultipliedAlpha: true,
      transparent: true,
    });
    materialRef.current = material;

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    // Create render target for ASCII mode
    const renderTarget = new THREE.WebGLRenderTarget(1, 1, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
    });
    renderTargetRef.current = renderTarget;

    // Create font atlas
    const fontAtlas = createFontAtlas(ASCII_CHARS, 16, 24);
    fontAtlasRef.current = fontAtlas;

    // Create ASCII post-processing material
    const asciiMaterial = new THREE.ShaderMaterial({
      vertexShader: vert,
      fragmentShader: asciiFrag,
      uniforms: {
        uScene: { value: renderTarget.texture },
        uFontAtlas: { value: fontAtlas },
        uCanvas: { value: new THREE.Vector2(1, 1) },
        uCellSize: { value: new THREE.Vector2(asciiCellSize, asciiCellSize * 1.5) },
        uCharCount: { value: ASCII_CHARS.length },
        uTransparent: { value: transparent ? 1 : 0 },
      },
      premultipliedAlpha: true,
      transparent: true,
    });
    asciiMaterialRef.current = asciiMaterial;

    const asciiMesh = new THREE.Mesh(geometry, asciiMaterial);
    asciiScene.add(asciiMesh);

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
      alpha: true,
    });
    rendererRef.current = renderer;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, transparent ? 0 : 1);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    container.appendChild(renderer.domElement);

    const clock = new THREE.Clock();

    const applySize = (w: number, h: number) => {
      renderer.setSize(w, h, false);
      renderTarget.setSize(w, h);
      (material.uniforms.uCanvas.value as THREE.Vector2).set(w, h);
      (asciiMaterial.uniforms.uCanvas.value as THREE.Vector2).set(w, h);

      if (asciiMaterial.uniforms.uTransparent.value) {
        renderer.setRenderTarget(renderTarget);
        renderer.render(scene, camera);
        renderer.setRenderTarget(null);
        renderer.render(asciiScene, camera);
      } else {
        renderer.render(scene, camera);
      }
    };

    applySize(container.clientWidth || 1, container.clientHeight || 1);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      applySize(Math.round(width) || 1, Math.round(height) || 1);
    });
    ro.observe(container);

    const TARGET_FPS = 15;
    const FRAME_INTERVAL = 1000 / TARGET_FPS;
    let lastFrameTime = 0;

    const loop = (now: number) => {
      rafRef.current = requestAnimationFrame(loop);

      const delta = now - lastFrameTime;
      if (delta < FRAME_INTERVAL) return;
      lastFrameTime = now - (delta % FRAME_INTERVAL);

      clock.getDelta();
      const elapsed = clock.elapsedTime;
      material.uniforms.uTime.value = elapsed;

      const deg = (rotationRef.current % 360) + autoRotateRef.current * elapsed;
      const rad = (deg * Math.PI) / 180;
      const c = Math.cos(rad);
      const s = Math.sin(rad);
      (material.uniforms.uRot.value as THREE.Vector2).set(c, s);

      if (asciiMaterial.uniforms.uTransparent.value) {
        // Render with ASCII post-processing
        renderer.setRenderTarget(renderTarget);
        renderer.render(scene, camera);
        renderer.setRenderTarget(null);
        renderer.render(asciiScene, camera);
      } else {
        // Render directly without ASCII
        renderer.render(scene, camera);
      }
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      geometry.dispose();
      material.dispose();
      asciiMaterial.dispose();
      renderTarget.dispose();
      fontAtlas.dispose();
      renderer.dispose();
      if (renderer.domElement && renderer.domElement.parentElement === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  useEffect(() => {
    const material = materialRef.current;
    const asciiMaterial = asciiMaterialRef.current;
    const renderer = rendererRef.current;
    if (!material || !asciiMaterial) return;

    rotationRef.current = rotation;
    autoRotateRef.current = autoRotate;
    material.uniforms.uSpeed.value = speed;
    material.uniforms.uScale.value = scale;
    material.uniforms.uFrequency.value = frequency;
    material.uniforms.uWarpStrength.value = warpStrength;
    material.uniforms.uNoise.value = noise;
    material.uniforms.uCenterRadius.value = centerRadius;
    material.uniforms.uCenterFalloff.value = centerFalloff;

    const toVec3 = (hex: string) => {
      const h = hex.replace('#', '').trim();
      const v =
        h.length === 3
          ? [parseInt(h[0] + h[0], 16), parseInt(h[1] + h[1], 16), parseInt(h[2] + h[2], 16)]
          : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
      return new THREE.Vector3(v[0] / 255, v[1] / 255, v[2] / 255);
    };

    const arr = (colors || []).filter(Boolean).slice(0, MAX_COLORS).map(toVec3);
    for (let i = 0; i < MAX_COLORS; i++) {
      const vec = (material.uniforms.uColors.value as THREE.Vector3[])[i];
      if (i < arr.length) vec.copy(arr[i]);
      else vec.set(0, 0, 0);
    }
    material.uniforms.uColorCount.value = arr.length;

    material.uniforms.uTransparent.value = transparent ? 1 : 0;
    asciiMaterial.uniforms.uTransparent.value = ascii ? 1 : 0;
    (asciiMaterial.uniforms.uCellSize.value as THREE.Vector2).set(
      asciiCellSize,
      asciiCellSize * 1.5
    );

    if (renderer) renderer.setClearColor(0x000000, transparent ? 0 : 1);
  }, [
    rotation,
    autoRotate,
    speed,
    scale,
    frequency,
    warpStrength,
    noise,
    colors,
    transparent,
    centerRadius,
    centerFalloff,
    ascii,
    asciiCellSize,
  ]);

  const debugPanel =
    debug &&
    typeof document !== 'undefined' &&
    createPortal(
      <div className="fixed top-2 right-2 z-[99999] bg-black/90 border border-white/10 rounded p-2 text-white text-[10px] font-mono w-48">
        <label className="flex items-center gap-1.5 mb-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={dAscii}
            onChange={(e) => setDAscii(e.target.checked)}
            className="w-3 h-3"
          />
          <span>ASCII</span>
        </label>
        <DebugSlider
          label="spd"
          value={dSpeed}
          onChange={setDSpeed}
          min={0}
          max={0.5}
          step={0.01}
        />
        <DebugSlider label="scl" value={dScale} onChange={setDScale} min={0.1} max={2} step={0.1} />
        <DebugSlider
          label="frq"
          value={dFrequency}
          onChange={setDFrequency}
          min={0.1}
          max={5}
          step={0.1}
        />
        <DebugSlider
          label="wrp"
          value={dWarpStrength}
          onChange={setDWarpStrength}
          min={0}
          max={2}
          step={0.1}
        />
        <DebugSlider
          label="nse"
          value={dNoise}
          onChange={setDNoise}
          min={0}
          max={0.5}
          step={0.01}
        />
        <DebugSlider
          label="rad"
          value={dCenterRadius}
          onChange={setDCenterRadius}
          min={0}
          max={1}
          step={0.05}
        />
        <DebugSlider
          label="fal"
          value={dCenterFalloff}
          onChange={setDCenterFalloff}
          min={0}
          max={1}
          step={0.05}
        />
        <DebugSlider
          label="cel"
          value={dAsciiCellSize}
          onChange={setDAsciiCellSize}
          min={4}
          max={24}
          step={1}
        />
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={containerRef}
        className={`w-full h-full relative overflow-hidden ${className}`}
        style={style}
      />
      {debugPanel}
    </>
  );
}

function DebugSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
}) {
  return (
    <div className="flex items-center gap-1.5 mb-1">
      <span className="text-white/50 w-6">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 h-0.5 bg-white/20 rounded appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2 [&::-webkit-slider-thumb]:h-2 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full"
      />
      <span className="text-white/40 w-7 text-right">{value.toFixed(2)}</span>
    </div>
  );
}
